import {
    Card,
    CardContent,
    CardHeader, FormControl,
    Grid,
    IconButton, Input, InputAdornment,
    Paper, Table,
    TableBody,
    TableCell,
    TableContainer, TableFooter,
    TableHead, TableSortLabel, ToggleButton, ToggleButtonGroup, Tooltip
} from "@mui/material";
import theme2 from "../../../lib/theme";
import * as React from "react";
import CachedIcon from "@mui/icons-material/Cached";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Loading from "../../../components/loading";
import TableHeaderRow from "../../../components/TableHeaderRow";
import StripedTableRow from "../../../components/StripedTableRow";
import HttpsIcon from "@mui/icons-material/Https";
import TableFooterRow from "../../../components/TableFooterRow";
import moment from "moment";

const defaultSummary = {leads_sold: 0, revenue: 0, revenue_per_lead: 0};
export default function Brokers(params: {dateRange: any, reload: any, verticals: string[]}) {
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState([] as any[]);
    const [search, setSearch] = React.useState('');
    const [filtered, setFiltered] = React.useState([] as any[]);
    const [summary, setSummary] = React.useState({...defaultSummary} as any);
    const [sort, setSort] = React.useState({column: 'leads_generated', sortAsc: false});

    const reload = async () => {
        setLoading(true);

        // do a post to https://membertool.banneredgemedia.com/integration/publishers

        const s = moment.tz(moment(params.dateRange[0]).format("YYYY-MM-DD"), "America/Los_Angeles").format();
        let e = moment(params.dateRange[1]).format();
        let dash = e.lastIndexOf("-");
        e = e.substring(0, dash);
        e = moment.tz(e, "America/Los_Angeles").format();

        const response = await fetch('https://membertool.banneredgemedia.com/integration/brokers', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                startDate: s,
                endDate: e,
                verticals: params.verticals
            })
        });

        const json = await response.json();
        setData(json);
        calcFiltered(json, search, sort);

        setLoading(false);
    }


    React.useEffect(() => {
        if (params.reload) {
            reload().then(() => {});
        }
    }, [params.reload]);

    const calcFiltered = (_data: any[], _search: string, _sort: any) => {
        let dat = _data.filter((d: any) => !_search.trim() || d.broker.toLowerCase().includes(_search.trim().toLowerCase()));

        dat = dat.sort((a: any, b: any) => {
            if (_sort.sortAsc === false) {
                if (a[_sort.column] < b[_sort.column]) {
                    return 1;
                } else {
                    return -1;
                }
            } else {
                if (a[_sort.column] > b[_sort.column]) {
                    return 1;
                } else {
                    return -1;
                }
            }
        });

        setFiltered(dat);

        let sum = {...defaultSummary};
        dat.forEach((d: any) => {
            sum.leads_sold += d.leads_sold;
            sum.revenue += d.revenue;
        });
        sum.revenue_per_lead = sum.leads_sold ? sum.revenue / sum.leads_sold : 0;
        setSummary(sum);
    }

    const doSort = (column: string, defaultAsc: boolean = false) => {
        let s;
        if (sort.column === column) {
            s = {column: column, sortAsc: !sort.sortAsc};
        } else {
            s = {column: column, sortAsc: defaultAsc};
        }
        setSort(s);

        calcFiltered(data, search, s);
    }

    return (
        <Card sx={{height: '463px', maxWidth: 'calc(100vw - 10px)'}}>
            <CardHeader title={"Brokers"} sx={{backgroundColor: theme2.palette.primary.main, color: theme2.palette.primary.contrastText, padding: '5px'}}
                        titleTypographyProps={{sx:{fontSize: '18px'}}}
                        action={
                <>
                    <IconButton aria-label="refresh" sx={{color: theme2.palette.primary.contrastText}} onClick={reload}>
                        <CachedIcon />
                    </IconButton>
                </>
                        }
            />
            <CardContent>
                {loading ? <Box sx={{marginTop: '130px'}}><Loading/></Box> : <>
                    <FormControl variant="outlined" sx={{ m: 1, maxWidth: 190 }} size="small">
                        <Input
                            id="search"
                            type="text"
                            spellCheck="false"
                            placeholder="Search"
                            value={search || ''}
                            onChange={(e: any) => {setSearch(e.target.value); calcFiltered(data, e.target.value, sort)}}
                            startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>}
                        />
                    </FormControl>

                    <TableContainer component={Paper} sx={{maxWidth: 'calc(100vw - 40px)', overflowY: 'auto', maxHeight: '333px'}}>
                        <Table size="small"
                               sx={{
                                   "& .MuiTableRow-root:hover": {
                                       backgroundColor: "primary.light"
                                   }
                               }}
                               stickyHeader={true}
                        >
                            <TableHead>
                                <TableHeaderRow>
                                    <TableCell>
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'broker'}
                                            direction={sort.column === 'broker' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('broker', true)}}
                                        >
                                            Broker
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'leads_sold'}
                                            direction={sort.column === 'leads_sold' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('leads_sold', false)}}
                                        >
                                            Sold
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'revenue'}
                                            direction={sort.column === 'revenue' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('revenue', false)}}
                                        >
                                            Rev
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            sx={{width: '100%'}}
                                            active={sort.column === 'revenue_per_lead'}
                                            direction={sort.column === 'revenue_per_lead' && !sort.sortAsc ? 'desc' : 'asc'}
                                            onClick={() => {doSort('revenue_per_lead', false)}}
                                        >
                                            RPL
                                        </TableSortLabel>
                                    </TableCell>
                                </TableHeaderRow>
                            </TableHead>
                            <TableBody>
                                {filtered.map((row: any) => (
                                    <StripedTableRow key={row.publisher_id}>
                                        <TableCell sx={{minWidth: '200px'}}>{row.broker}</TableCell>
                                        <TableCell align="right">{row.leads_sold.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}</TableCell>
                                        <TableCell align="right">${row.revenue.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                        <TableCell align="right">${row.revenue_per_lead.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                    </StripedTableRow>
                                ))}
                                </TableBody>
                            <TableFooter>
                                <TableFooterRow>
                                    <TableCell>Totals</TableCell>
                                    <TableCell align="right">{summary.leads_sold.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}</TableCell>
                                    <TableCell align="right">${summary.revenue.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                    <TableCell align="right">${summary.revenue_per_lead.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                </TableFooterRow>
                            </TableFooter>
                        </Table>

                    </TableContainer>
                </>}
            </CardContent>
        </Card>
    );
}
